import { useEffect, useState, useCallback } from "react";
import api, { currency, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Building2, Users, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Branches() {
    const { user } = useAuth();
    const [rollup, setRollup] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", city: "", address: "", phone: "" });
    const canCreate = user.role === "ceo";

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(canCreate ? "/branches/rollup" : "/branches");
            setRollup(data);
        } catch (e) { /* silent */ }
    }, [canCreate]);
    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/branches", form);
            toast.success("Branch created");
            setOpen(false);
            setForm({ name: "", city: "", address: "", phone: "" });
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6" data-testid="branches-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Multi-Tenant</p>
                    <h1 className="font-serif text-3xl text-komma-navy">Branch Roll-up</h1>
                    <p className="text-sm text-gray-600 mt-1">CEO-only view of every Komma Associates office and its portfolio.</p>
                </div>
                {canCreate && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-komma-navy hover:bg-komma-navy-light" data-testid="add-branch-btn">
                                <Plus size={16} className="mr-2" /> New Branch
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle className="font-serif text-komma-navy">Open a new Branch</DialogTitle></DialogHeader>
                            <form onSubmit={create} className="space-y-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Branch name</Label>
                                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10" data-testid="branch-name" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">City</Label>
                                        <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-10" data-testid="branch-city" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Phone</Label>
                                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10" data-testid="branch-phone" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Address</Label>
                                    <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="branch-address" />
                                </div>
                                <Button type="submit" className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="branch-submit">Save Branch</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {rollup.map((b) => (
                    <div key={b.id} className="komma-card p-6 relative overflow-hidden" data-testid={`branch-${b.id}`}>
                        <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-komma-gold/10" />
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-md bg-komma-navy text-komma-gold flex items-center justify-center">
                                <Building2 size={18} />
                            </div>
                            <div>
                                <p className="font-serif text-lg text-komma-navy">{b.name}</p>
                                <p className="text-xs text-gray-500">{b.city}</p>
                            </div>
                        </div>
                        <div className="komma-gold-hairline my-4" />
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <MiniStat icon={Users} label="Customers" value={b.customers ?? "—"} />
                            <MiniStat icon={Wallet} label="Loans" value={b.loans ?? "—"} />
                        </div>
                        <div className="mt-4">
                            <p className="text-[10px] uppercase tracking-widest text-gray-500">Pipeline</p>
                            <p className="font-serif text-xl text-komma-navy mt-1">{currency(b.pipeline_value)}</p>
                            <p className="text-xs text-komma-gold-dark mt-1">Disbursed {currency(b.disbursed_value)}</p>
                        </div>
                        <p className="mt-3 text-xs text-gray-500">{b.address}</p>
                    </div>
                ))}
                {rollup.length === 0 && (
                    <div className="col-span-full komma-card p-12 text-center">
                        <Building2 className="mx-auto text-komma-gold" size={40} />
                        <p className="font-serif text-xl text-komma-navy mt-3">No branches yet</p>
                        <p className="text-sm text-gray-500 mt-1">Add your first Komma Associates office to segregate data.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function MiniStat({ icon: Icon, label, value }) {
    return (
        <div className="rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-2 text-gray-500">
                <Icon size={12} />
                <span className="text-[10px] uppercase tracking-widest">{label}</span>
            </div>
            <p className="font-serif text-xl text-komma-navy mt-1">{value}</p>
        </div>
    );
}
